<script setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMenu } from '@/core/composables/menu.composable'
import { useUIStore } from '@/core/stores/ui.store'
import { BRADLEY_ANALYTICS_EVENTS } from '@/modules/new-bradley/analytics/bradley-analytics.constants.js'
import { useAnalytics } from '@/shared/analytics'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  depth: {
    type: Number,
    default: 0,
  },
})

const route = useRoute()
const router = useRouter()
const { toggleExpand, isExpanded } = useMenu()
const uiStore = useUIStore()
const { bradleyPanelOpen } = storeToRefs(uiStore)
const { trackEvent } = useAnalytics()

const isActive = computed(() => {
  if (props.item.opensPanel === 'bradley') {
    return bradleyPanelOpen.value
  }

  if (props.item.children?.length) {
    return props.item.children.some((child) => {
      if (typeof child.route === 'object') {
        // Handle route objects (for dashboards)
        return (
          route.name === child.route.name
          && route.params.dashboardId === child.route.params.dashboardId
        )
      }
      // Handle string routes
      return (
        route.path === child.route
        || (child.route !== props.item.route && route.path.startsWith(child.route))
      )
    })
  }

  if (typeof props.item.route === 'object') {
    // Handle route objects (for dashboards)
    return (
      route.name === props.item.route.name
      && route.params.dashboardId === props.item.route.params.dashboardId
    )
  }
  // Handle string routes
  return route.path === props.item.route
})

const getDepthPadding = computed(() => {
  if (props.item.type !== 'sub')
    return ''

  const paddingClasses = {
    0: 'pl-4',
    1: 'pl-8',
    2: 'pl-12',
  }

  return paddingClasses[props.depth] || paddingClasses[4]
})

const menuItemClasses = computed(() => {
  const baseClasses = {
    'flex items-center gap-x-2 mx-6 cursor-pointer': true,
    'text-white': isActive.value,
    'text-dss-surface-menu': !isActive.value,
  }

  if (props.item.type === 'sub') {
    if (props.depth === 1) {
      return {
        ...baseClasses,
        '*:data-label:-mx-4 *:data-label:px-4 *:data-label:py-2': true,
        'text-white *:data-label:border *:data-label:border-[#484A81B0] *:data-label:bg-dss-secondary-400/60 *:data-label:rounded-lg':
        isActive.value,
        [getDepthPadding.value]: true,
      }
    }
    else {
      return {
        ...baseClasses,
        'py-2': true,
        'decoration-1 underline': isActive.value,
        [getDepthPadding.value]: true,
      }
    }
  }

  return {
    ...baseClasses,
    'rounded-lg py-2 font-bold': true,
  }
})

const showChildren = computed(() => {
  return isExpanded(props.item) && props.item.children?.length > 0
})

function handleClick() {
  if (props.item.opensPanel === 'bradley') {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.ASK_BRADLEY_AI_BUTTON, { source: 'side_menu' })
    uiStore.openBradleyPanel()
    return
  }

  if (props.item.children?.length) {
    toggleExpand(props.item)
    return
  }

  if (props.item.route && route.path !== props.item.route) {
    router.push(props.item.route).catch(() => {})
  }
}
</script>

<template>
  <div>
    <component
      :is="item.opensPanel ? 'div' : (item.route && !item.children?.length ? 'router-link' : 'div')"
      :to="item.opensPanel ? undefined : item.route"
      :class="menuItemClasses"
      @click="handleClick"
    >
      <span
        v-if="item.icon"
        class="size-6 shrink-0 flex items-center justify-center"
      >
        <div class="relative">
          <i :class="`pi pi-${item.icon} text-lg font-medium`" />
        </div>
      </span>

      <div
        data-label
        class="text-sm/4 w-full"
      >
        {{ item.isRawName ? item.name : $t(item.name) }}
      </div>
    </component>

    <template v-if="showChildren">
      <dss-menu-item
        v-for="child in item.children"
        :key="child.name"
        :item="child"
        :depth="depth + 1"
      />
    </template>
  </div>
</template>
